/**
 * htmldiff.js is a library that compares HTML content. It creates a diff between two
 * HTML documents by combining the two documents and wrapping the differences with
 * <ins> and <del> tags. Here is a high-level overview of how the diff works.
 *
 * 1. Tokenize the before and after HTML with htmlToTokens.
 * 2. Generate a list of operations that convert the before list of tokens to the after
 *    list of tokens with calculateOperations, which does the following:
 *      a. Find all the matching blocks of tokens between the before and after lists of
 *         tokens with findMatchingBlocks. This is done by finding the single longest
 *         matching block with findMatch, then iteratively finding the next longest
 *         matching blocks that precede and follow the longest matching block.
 *      b. Determine insertions, deletions, and replacements from the matching blocks.
 *         This is done in calculateOperations.
 * 3. Render the list of operations by wrapping tokens with <ins> and <del> tags where
 *    appropriate with renderOperations.
 *
 * Example usage:
 *
 *   var htmldiff = require('htmldiff.js');
 *
 *   htmldiff('<p>this is some text</p>', '<p>this is some more text</p>')
 *   == '<p>this is some <ins>more </ins>text</p>'
 *
 *   htmldiff('<p>this is some text</p>', '<p>this is some more text</p>', 'diff-class')
 *   == '<p>this is some <ins class="diff-class">more </ins>text</p>'
 */
function isEndOfTag(char: string) {
  return char === '>';
}

function isStartOfTag(char: string) {
  return char === '<';
}

function isWhitespace(char: string) {
  return /^\s+$/.test(char);
}

function isTag(token: string) {
  let match = token.match(/^\s*<([^!>][^>]*)>\s*$/);
  return !!match && match[1].trim().split(' ')[0];
}

function isntTag(token: string) {
  return !isTag(token);
}

function isStartofHTMLComment(word: string) {
  return /^<!--/.test(word);
}

function isEndOfHTMLComment(word: string) {
  return /-->$/.test(word);
}

function isStartOfAtomicTag(word: string) {
  let result = /^<(iframe|object|math|svg|script)/.exec(word);
  return result && result[1];
}

function isEndOfAtomicTag(word: string, tag: string) {
  return word.substring(word.length - tag.length - 2) === '</' + tag;
}

function isVoidTag(token: string) {
  return /^\s*<[^>]+\/>\s*$/.test(token);
}

function isWrappable(token: string) {
  return isntTag(token) || isStartOfAtomicTag(token) || isVoidTag(token);
}

export type Token = {
  string: string;
  key: string;
};

function createToken(currentWord: string): Token {
  return {
    string: currentWord,
    key: getKeyForToken(currentWord),
  };
}

class Match {
  segment: Segment;
  length: number;
  startInBefore: number;
  startInAfter: number;
  endInBefore: number;
  endInAfter: number;

  segmentStartInBefore: number;
  segmentStartInAfter: number;
  segmentEndInBefore: number;
  segmentEndInAfter: number;
  constructor(
    startInBefore: number,
    startInAfter: number,
    length: number,
    segment: Segment
  ) {
    this.segment = segment;
    this.length = length;

    this.startInBefore = startInBefore + segment.beforeIndex;
    this.startInAfter = startInAfter + segment.afterIndex;
    this.endInBefore = this.startInBefore + this.length - 1;
    this.endInAfter = this.startInAfter + this.length - 1;

    this.segmentStartInBefore = startInBefore;
    this.segmentStartInAfter = startInAfter;
    this.segmentEndInBefore = this.segmentStartInBefore + this.length - 1;
    this.segmentEndInAfter = this.segmentStartInAfter + this.length - 1;
  }
}

export function htmlToTokens(html: string) {
  let mode = 'char';
  let currentWord = '';
  let currentAtomicTag = '';
  let words: Array<Token> = [];
  for (let i = 0; i < html.length; i++) {
    let char = html[i];
    if (mode === 'tag') {
      const atomicTag = isStartOfAtomicTag(currentWord);
      if (atomicTag) {
        mode = 'atomic_tag';
        currentAtomicTag = atomicTag;
        currentWord += char;
      } else if (isStartofHTMLComment(currentWord)) {
        mode = 'html_comment';
        currentWord += char;
      } else if (isEndOfTag(char)) {
        currentWord += '>';
        words.push(createToken(currentWord));
        currentWord = '';
        if (isWhitespace(char)) {
          mode = 'whitespace';
        } else {
          mode = 'char';
        }
      } else {
        currentWord += char;
      }
    } else if (mode === 'atomic_tag') {
      if (isEndOfTag(char) && isEndOfAtomicTag(currentWord, currentAtomicTag)) {
        currentWord += '>';
        words.push(createToken(currentWord));
        currentWord = '';
        currentAtomicTag = '';
        mode = 'char';
      } else {
        currentWord += char;
      }
    } else if (mode === 'html_comment') {
      currentWord += char;
      if (isEndOfHTMLComment(currentWord)) {
        currentWord = '';
        mode = 'char';
      }
    } else if (mode === 'char') {
      if (isStartOfTag(char)) {
        if (currentWord) {
          words.push(createToken(currentWord));
        }
        currentWord = '<';
        mode = 'tag';
      } else if (/\s/.test(char)) {
        if (currentWord) {
          words.push(createToken(currentWord));
        }
        currentWord = char;
        mode = 'whitespace';
      } else if (/[\w\d#@]/.test(char)) {
        currentWord += char;
      } else if (/&/.test(char)) {
        if (currentWord) {
          words.push(createToken(currentWord));
        }
        currentWord = char;
      } else {
        currentWord += char;
        words.push(createToken(currentWord));
        currentWord = '';
      }
    } else if (mode === 'whitespace') {
      if (isStartOfTag(char)) {
        if (currentWord) {
          words.push(createToken(currentWord));
        }
        currentWord = '<';
        mode = 'tag';
      } else if (isWhitespace(char)) {
        if (currentWord) {
          words.push(createToken(currentWord));
        }
        currentWord = char;
        mode = 'whitespace';
      } else {
        if (currentWord) {
          words.push(createToken(currentWord));
        }
        currentWord = char;
        mode = 'char';
      }
    } else {
      throw new Error('Unknown mode ' + mode);
    }
  }
  if (currentWord) {
    words.push(createToken(currentWord));
  }
  return words;
}

function getKeyForToken(token: string) {
  let tagName = /<([^\s>]+)[\s>]/.exec(token);
  if (tagName) {
    return '<' + tagName[1].toLowerCase() + '>';
  }
  return token && token.replace(/(\s+|&nbsp;|&#160;)/g, ' ');
}

function createMap(tokens: Array<Token>): SegmentMap {
  return tokens.reduce(function (map, token, index) {
    if (map[token.key]) {
      map[token.key].push(index);
    } else {
      map[token.key] = [index];
    }
    return map;
  }, {});
}

/**
 * Compares two match objects to determine if the second match object comes before or after the
 * first match object. Returns -1 if the m2 should come before m1. Returns 1 if m1 should come
 * before m2. If the two matches criss-cross each other, a null is returned.
 *
 * Returns -1 if the m2 should come before m1. Returns 1 if m1 should come
 *    before m2. If the two matches criss-cross each other, 0 is returned.
 */
function compareMatches(m1: Match, m2: Match) {
  if (m2.endInBefore < m1.startInBefore && m2.endInAfter < m1.startInAfter) {
    return -1;
  } else if (
    m2.startInBefore > m1.endInBefore &&
    m2.startInAfter > m1.endInAfter
  ) {
    return 1;
  } else {
    return 0;
  }
}

type BinaryMatchTree = {
  value: Match;
  left: BinaryMatchTree | null;
  right: BinaryMatchTree | null;
};
class MatchBinarySearchTree {
  root: BinaryMatchTree | null = null;
  add(value: Match) {
    // Create the node to hold the match value.
    let node: BinaryMatchTree = {
      value: value,
      left: null,
      right: null,
    };

    let current = this.root;
    if (current) {
      let breakingCondition = true;
      while (breakingCondition) {
        // Determine if the match value should go to the left or right of the current
        // node.
        let position = compareMatches(current.value, value);
        if (position === -1) {
          // The position of the match is to the left of this node.
          if (current.left) {
            current = current.left;
          } else {
            current.left = node;
            breakingCondition = false;
          }
        } else if (position === 1) {
          // The position of the match is to the right of this node.
          if (current.right) {
            current = current.right;
          } else {
            current.right = node;
            breakingCondition = false;
          }
        } else {
          // If 0 was returned from compareMatches, that means the node cannot
          // be inserted because it overlaps an existing node.
          breakingCondition = false;
        }
      }
    } else {
      // If no nodes exist in the tree, make this the root node.
      this.root = node;
    }
  }

  /**
   * Converts the binary search tree into an array using an in-order traversal.
   *
   * @return {Array.<Match>} An array containing the matches in the binary search tree.
   */
  toArray() {
    function inOrder(node: BinaryMatchTree | null, nodes: Array<Match>) {
      if (node) {
        inOrder(node.left, nodes);
        nodes.push(node.value);
        inOrder(node.right, nodes);
      }
      return nodes;
    }

    return inOrder(this.root, []);
  }
}

function findBestMatch(segment: Segment) {
  let beforeTokens = segment.beforeTokens;
  let afterMap = segment.afterMap;
  let lastSpace: number | undefined;
  let bestMatch: Match | undefined;

  // Iterate through the entirety of the beforeTokens to find the best match.
  for (let beforeIndex = 0; beforeIndex < beforeTokens.length; beforeIndex++) {
    let lookBehind = false;

    // If the current best match is longer than the remaining tokens, we can bail because we
    // won't find a better match.
    let remainingTokens = beforeTokens.length - beforeIndex;
    if (bestMatch != null) {
      if (remainingTokens < bestMatch.length) {
        break;
      }
    }

    // If the current token is whitespace, make a note of it and move on. Trying to start a
    // set of matches with whitespace is not efficient because it's too prevelant in most
    // documents. Instead, if the next token yields a match, we'll see if the whitespace can
    // be included in that match.
    let beforeToken = beforeTokens[beforeIndex];
    if (beforeToken.key === ' ') {
      lastSpace = beforeIndex;
      continue;
    }

    // Check to see if we just skipped a space, if so, we'll ask getFullMatch to look behind
    // by one token to see if it can include the whitespace.
    if (lastSpace === beforeIndex - 1) {
      lookBehind = true;
    }

    // If the current token is not found in the afterTokens, it won't match and we can move
    // on.
    let afterTokenLocations = afterMap[beforeToken.key];
    if (!afterTokenLocations) {
      continue;
    }

    // For each instance of the current token in afterTokens, let's see how big of a match
    // we can build.
    afterTokenLocations.forEach(function (afterIndex) {
      // getFullMatch will see how far the current token match will go in both
      // beforeTokens and afterTokens.
      let bestMatchLength = bestMatch ? bestMatch.length : 0;
      let match = getFullMatch(
        segment,
        beforeIndex,
        afterIndex,
        bestMatchLength,
        lookBehind
      );

      // If we got a new best match, we'll save it aside.
      if (match && match.length > bestMatchLength) {
        bestMatch = match;
      }
    });
  }

  return bestMatch;
}

function getFullMatch(
  segment: Segment,
  beforeStart: number,
  afterStart: number,
  minLength: number,
  lookBehind: boolean
) {
  let beforeTokens = segment.beforeTokens;
  let afterTokens = segment.afterTokens;

  // If we already have a match that goes to the end of the document, no need to keep looking.
  let minBeforeIndex = beforeStart + minLength;
  let minAfterIndex = afterStart + minLength;
  if (
    minBeforeIndex >= beforeTokens.length ||
    minAfterIndex >= afterTokens.length
  ) {
    return;
  }

  // If a minLength was provided, we can do a quick check to see if the tokens after that
  // length match. If not, we won't be beating the previous best match, and we can bail out
  // early.
  if (minLength) {
    let nextBeforeWord = beforeTokens[minBeforeIndex].key;
    let nextAfterWord = afterTokens[minAfterIndex].key;
    if (nextBeforeWord !== nextAfterWord) {
      return;
    }
  }

  // Extend the current match as far foward as it can go, without overflowing beforeTokens or
  // afterTokens.
  let searching = true;
  let currentLength = 1;
  let beforeIndex = beforeStart + currentLength;
  let afterIndex = afterStart + currentLength;

  while (
    searching &&
    beforeIndex < beforeTokens.length &&
    afterIndex < afterTokens.length
  ) {
    let beforeWord = beforeTokens[beforeIndex].key;
    let afterWord = afterTokens[afterIndex].key;
    if (beforeWord === afterWord) {
      currentLength++;
      beforeIndex = beforeStart + currentLength;
      afterIndex = afterStart + currentLength;
    } else {
      searching = false;
    }
  }

  // If we've been asked to look behind, it's because both beforeTokens and afterTokens may
  // have a whitespace token just behind the current match that was previously ignored. If so,
  // we'll expand the current match to include it.
  if (lookBehind && beforeStart > 0 && afterStart > 0) {
    let prevBeforeKey = beforeTokens[beforeStart - 1].key;
    let prevAfterKey = afterTokens[afterStart - 1].key;
    if (prevBeforeKey === ' ' && prevAfterKey === ' ') {
      beforeStart--;
      afterStart--;
      currentLength++;
    }
  }

  return new Match(beforeStart, afterStart, currentLength, segment);
}

type SegmentMap = Record<string, Array<number>>;

type Segment = {
  beforeTokens: Array<Token>;
  afterTokens: Array<Token>;
  beforeMap: SegmentMap;
  afterMap: SegmentMap;
  beforeIndex: number;
  afterIndex: number;
};

function createSegment(
  beforeTokens: Array<Token>,
  afterTokens: Array<Token>,
  beforeIndex: number,
  afterIndex: number
) {
  return {
    beforeTokens: beforeTokens,
    afterTokens: afterTokens,
    beforeMap: createMap(beforeTokens),
    afterMap: createMap(afterTokens),
    beforeIndex: beforeIndex,
    afterIndex: afterIndex,
  };
}

function findMatchingBlocks(segmentForMatch: Segment) {
  // Create a binary search tree to hold the matches we find in order.
  let matches = new MatchBinarySearchTree();
  let match;
  let segments = [segmentForMatch];

  // Each time the best match is found in a segment, zero, one or two new segments may be
  // created from the parts of the original segment not included in the match. We will
  // continue to iterate until all segments have been processed.
  while (segments.length > 0) {
    const segment = segments.pop();
    if (segment == null) {
      continue;
    }
    match = findBestMatch(segment);

    if (match && match.length) {
      // If there's an unmatched area at the start of the segment, create a new segment
      // from that area and throw it into the segments array to get processed.
      if (match.segmentStartInBefore > 0 && match.segmentStartInAfter > 0) {
        let leftBeforeTokens = segment.beforeTokens.slice(
          0,
          match.segmentStartInBefore
        );
        let leftAfterTokens = segment.afterTokens.slice(
          0,
          match.segmentStartInAfter
        );

        segments.push(
          createSegment(
            leftBeforeTokens,
            leftAfterTokens,
            segment.beforeIndex,
            segment.afterIndex
          )
        );
      }

      // If there's an unmatched area at the end of the segment, create a new segment from that
      // area and throw it into the segments array to get processed.
      let rightBeforeTokens = segment.beforeTokens.slice(
        match.segmentEndInBefore + 1
      );
      let rightAfterTokens = segment.afterTokens.slice(
        match.segmentEndInAfter + 1
      );
      let rightBeforeIndex = segment.beforeIndex + match.segmentEndInBefore + 1;
      let rightAfterIndex = segment.afterIndex + match.segmentEndInAfter + 1;

      if (rightBeforeTokens.length && rightAfterTokens.length) {
        segments.push(
          createSegment(
            rightBeforeTokens,
            rightAfterTokens,
            rightBeforeIndex,
            rightAfterIndex
          )
        );
      }

      matches.add(match);
    }
  }

  return matches.toArray();
}

/**
 * Gets a list of operations required to transform the before list of tokens into the
 * after list of tokens. An operation describes whether a particular list of consecutive
 * tokens are equal, replaced, inserted, or deleted.
 */

type Operation =
  | ReplaceOperation
  | NoneOperation
  | EqualOperation
  | InsertOperation
  | DeleteOperation;

type NoneOperation = {
  action: 'none';
  startInBefore: null;
  endInBefore: null;
  startInAfter: null;
  endInAfter: null;
};

type EqualOperation = {
  action: 'equal';
  startInBefore: number;
  endInBefore: number;
  startInAfter: number;
  endInAfter: number;
};

type InsertOperation = {
  action: 'insert';
  startInBefore: number;
  endInBefore: null;
  startInAfter: number;
  endInAfter: number;
};

type DeleteOperation = {
  action: 'delete';
  startInBefore: number;
  endInBefore: number;
  startInAfter: number;
  endInAfter: null;
};
type ReplaceOperation = {
  action: 'replace';
  startInBefore: number;
  endInBefore: number;
  startInAfter: number;
  endInAfter: number;
};

export function calculateOperations(
  beforeTokens: Array<Token>,
  afterTokens: Array<Token>
) {
  if (!beforeTokens) throw new Error('Missing beforeTokens');
  if (!afterTokens) throw new Error('Missing afterTokens');

  let positionInBefore = 0;
  let positionInAfter = 0;
  let operations: Array<Operation> = [];
  let segment = createSegment(beforeTokens, afterTokens, 0, 0);
  let matches = findMatchingBlocks(segment);
  matches.push(new Match(beforeTokens.length, afterTokens.length, 0, segment));

  for (let index = 0; index < matches.length; index++) {
    let match = matches[index];
    let actionUpToMatchPositions:
      | 'replace'
      | 'insert'
      | 'delete'
      | 'equal'
      | 'none' = 'none';
    if (positionInBefore === match.startInBefore) {
      if (positionInAfter !== match.startInAfter) {
        actionUpToMatchPositions = 'insert';
      }
    } else {
      actionUpToMatchPositions = 'delete';
      if (positionInAfter !== match.startInAfter) {
        actionUpToMatchPositions = 'replace';
      }
    }
    if (actionUpToMatchPositions !== 'none') {
      if (actionUpToMatchPositions === 'replace') {
        operations.push({
          action: actionUpToMatchPositions,
          startInBefore: positionInBefore,
          endInBefore: match.startInBefore - 1,
          startInAfter: positionInAfter,
          endInAfter: match.startInAfter - 1,
        });
      }
      if (actionUpToMatchPositions === 'insert') {
        operations.push({
          action: actionUpToMatchPositions,
          startInBefore: positionInBefore,
          endInBefore: null,
          startInAfter: positionInAfter,
          endInAfter: match.startInAfter - 1,
        });
      }

      if (actionUpToMatchPositions === 'delete') {
        operations.push({
          action: actionUpToMatchPositions,
          startInBefore: positionInBefore,
          endInBefore: match.startInBefore - 1,
          startInAfter: positionInAfter,
          endInAfter: null,
        });
      }
    }
    if (match.length !== 0) {
      operations.push({
        action: 'equal',
        startInBefore: match.startInBefore,
        endInBefore: match.endInBefore,
        startInAfter: match.startInAfter,
        endInAfter: match.endInAfter,
      });
    }
    positionInBefore = match.endInBefore + 1;
    positionInAfter = match.endInAfter + 1;
  }

  let postProcessed: Array<Operation> = [];
  let lastOp: Operation = {
    action: 'none',
    startInBefore: null,
    endInBefore: null,
    startInAfter: null,
    endInAfter: null,
  };

  function isSingleWhitespace(op: Operation) {
    if (op.action !== 'equal') {
      return false;
    }
    if (op.endInBefore - op.startInBefore !== 0) {
      return false;
    }
    return /^\s$/.test(
      beforeTokens.slice(op.startInBefore, op.endInBefore + 1)[0].string
    );
  }

  for (let i = 0; i < operations.length; i++) {
    let op = operations[i];

    if (
      lastOp.action === 'replace' &&
      ((op.action === 'equal' && isSingleWhitespace(op)) ||
        op.action === 'replace')
    ) {
      lastOp.endInBefore = op.endInBefore;
      lastOp.endInAfter = op.endInAfter;
    } else {
      postProcessed.push(op);
      lastOp = op;
    }
  }
  return postProcessed;
}

/**
 * A TokenWrapper provides a utility for grouping segments of tokens based on whether they're
 * wrappable or not. A tag is considered wrappable if it is closed within the given set of
 * tokens. For example, given the following tokens:
 *
 *      ['</b>', 'this', ' ', 'is', ' ', 'a', ' ', '<b>', 'test', '</b>', '!']
 *
 * The first '</b>' is not considered wrappable since the tag is not fully contained within the
 * array of tokens. The '<b>', 'test', and '</b>' would be a part of the same wrappable segment
 * since the entire bold tag is within the set of tokens.
 *
 * TokenWrapper has a method 'combine' which allows walking over the segments to wrap them in
 * tags.
 */

type WrapperNotes = {
  insertedTag: boolean;
  isWrappable: string | boolean;
};

type WrapperCombine = {
  isWrappable: string | boolean;
  tokens: Array<string>;
};

type TagStack = {
  tag: string;
  position: number;
};

const HTML_SELF_ENCLOSING_TAGS = [
  'img',
  'br',
  'area',
  'base',
  'col',
  'embed',
  'hr',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
  'iframe',
  'template',
];

export function isSelfEnclosingTag(tag: string) {
  return HTML_SELF_ENCLOSING_TAGS.indexOf(tag) !== -1;
}

class TokenWrapper {
  tokens: Array<string>;
  notes: Array<WrapperNotes>;
  constructor(tokens: Array<string>) {
    this.tokens = tokens;

    const initalReducer: {
      notes: Array<WrapperNotes>;
      tagStack: Array<TagStack>;
    } = {notes: [], tagStack: []};

    const reducer = tokens.reduce(function (data, token, index) {
      data.notes.push({
        isWrappable: isWrappable(token),
        insertedTag: false,
      });

      let tag = !isVoidTag(token) && isTag(token);
      let lastEntry = data.tagStack[data.tagStack.length - 1];
      if (tag) {
        if (lastEntry && '/' + lastEntry.tag === tag) {
          data.notes[lastEntry.position].insertedTag = true;
          data.tagStack.pop();
        } else if (isSelfEnclosingTag(tag)) {
          data.notes[index].insertedTag = true;
          data.notes[index].isWrappable = true;
        } else {
          data.tagStack.push({
            tag: tag,
            position: index,
          });
        }
      }
      return data;
    }, initalReducer);

    if (reducer.tagStack.length === 2) {
      // Sometimes the data is diffed incorrectly and the start of a best match is
      // found for an ending tag of the previous match. This makes the htmldiff
      // algorithm to "mix up" the opening and closing of the tags and is leaving 2
      // unprocessed nodes in ``tagStack``. To "mark" the tag properly - we should
      // check if the reversal of tagStack ordering is creating a html tag match.
      //
      // e.g. - the diff starts at ``</p>`` - and the delete operation contains
      // ``</p><p>[... rest of the delete operation]``. That operation will
      // theoretically leave an unclosed <p> tag. However - that <p> tag will be
      // closed on another ``equal`` node and the whole document will be intact
      // (this is something I encountered in a real scenario).
      //
      // This means that the document from the diff will be created nicely - however
      // - the ``p`` node in ``del`` operation will not have appropriate html markup
      // attached cause it was not processed in ``tagStack``.

      const lastEntry = reducer.tagStack[1];
      const firstEntry = reducer.tagStack[0];

      if (lastEntry && '/' + lastEntry.tag === firstEntry.tag) {
        reducer.notes[lastEntry.position].insertedTag = true;
        reducer.tagStack.pop();
        reducer.tagStack.pop();
      }
    }
    this.notes = reducer.notes;
  }

  combine(mapFn: (WrapperCombine) => string, tagFn: (string) => string) {
    let notes = this.notes;
    let tokens = this.tokens.slice();

    let initalCombinationData: {
      list: Array<WrapperCombine>;
      status: null | string | boolean;
      lastIndex: number;
    } = {
      list: [],
      status: null,
      lastIndex: 0,
    };

    let segments = tokens.reduce(function (data, token, index) {
      if (notes[index].insertedTag) {
        tokens[index] = tagFn(tokens[index]);
      }
      if (data.status === null) {
        data.status = notes[index].isWrappable;
      }
      let status = notes[index].isWrappable;
      if (status !== data.status) {
        data.list.push({
          isWrappable: data.status,
          tokens: tokens.slice(data.lastIndex, index),
        });
        data.lastIndex = index;
        data.status = status;
      }
      if (index === tokens.length - 1) {
        data.list.push({
          isWrappable: data.status,
          tokens: tokens.slice(data.lastIndex, index + 1),
        });
      }
      return data;
    }, initalCombinationData).list;

    return segments.map(mapFn).join('');
  }
}

function wrap(tag: string, content: Array<string>, opIndex: number) {
  let wrapper = new TokenWrapper(content);
  let attrs = ' data-operation-index="' + opIndex + '"';

  return wrapper.combine(
    function (segment: WrapperCombine) {
      if (segment.isWrappable) {
        let val = segment.tokens.join('');
        if (val.trim()) {
          return '<' + tag + attrs + '>' + val + '</' + tag + '>';
        }
      } else {
        return segment.tokens.join('');
      }
      return '';
    },
    function (openingTag: string) {
      let dataAttrs = ' data-diff-node="' + tag + '"';
      dataAttrs += ' data-operation-index="' + opIndex + '"';

      return openingTag.replace(/>\s*$/, dataAttrs + '$&');
    }
  );
}

let OPS = {
  equal: function (
    op: EqualOperation,
    beforeTokens: Array<Token>,
    afterTokens: Array<Token>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    opIndex: number
  ) {
    let tokens = afterTokens.slice(op.startInAfter, op.endInAfter + 1);
    return tokens.reduce(function (prev, curr) {
      return prev + curr.string;
    }, '');
  },
  insert: function (
    op: InsertOperation,
    beforeTokens: Array<Token>,
    afterTokens: Array<Token>,
    opIndex: number
  ) {
    let tokens = afterTokens.slice(op.startInAfter, op.endInAfter + 1);
    let val = tokens.map(function (token) {
      return token.string;
    });
    return wrap('ins', val, opIndex);
  },
  delete: function (
    op: DeleteOperation,
    beforeTokens: Array<Token>,
    afterTokens: Array<Token>,
    opIndex: number
  ) {
    let tokens = beforeTokens.slice(op.startInBefore, op.endInBefore + 1);
    let val = tokens.map(function (token) {
      return token.string;
    });
    return wrap('del', val, opIndex);
  },
  replace: function (
    op: ReplaceOperation,
    beforeTokens: Array<Token>,
    afterTokens: Array<Token>,
    opIndex: number
  ) {
    return (
      OPS['delete'].apply(null, [op, beforeTokens, afterTokens, opIndex]) +
      OPS['insert'].apply(null, [op, beforeTokens, afterTokens, opIndex])
    );
  },
};

/**
 * Renders a list of operations into HTML content. The result is the combined version
 * of the before and after tokens with the differences wrapped in tags.
 *
 * @param {Array.<string>} beforeTokens The before list of tokens.
 * @param {Array.<string>} afterTokens The after list of tokens.
 * @param {Array.<Object>} operations The list of operations to transform the before
 *      list of tokens into the after list of tokens, where each operation has the
 *      following keys:
 *      - {string} action One of {'replace', 'insert', 'delete', 'equal'}.
 *      - {number} startInBefore The beginning of the range in the list of before tokens.
 *      - {number} endInBefore The end of the range in the list of before tokens.
 *      - {number} startInAfter The beginning of the range in the list of after tokens.
 *      - {number} endInAfter The end of the range in the list of after tokens.
 * @param {string} dataPrefix (Optional) The prefix to use in data attributes.
 * @param {string} className (Optional) The class name to include in the wrapper tag.
 *
 * @return {string} The rendering of the list of operations.
 */
export function renderOperations(
  beforeTokens: Array<Token>,
  afterTokens: Array<Token>,
  operations: Array<Operation>
) {
  return operations.reduce(function (rendering, op, index) {
    return rendering + OPS[op.action](op, beforeTokens, afterTokens, index);
  }, '');
}

/**
 * Compares two pieces of HTML content and returns the combined content with differences
 * wrapped in <ins> and <del> tags.
 *
 * @param {string} before The HTML content before the changes.
 * @param {string} after The HTML content after the changes.
 *
 * @return {string} The combined HTML content with differences wrapped in <ins> and <del> tags.
 */
export function diff(before: string, after: string) {
  if (before === after) return before;

  const beforeTokens = htmlToTokens(before);
  const afterTokens = htmlToTokens(after);
  let ops = calculateOperations(beforeTokens, afterTokens);
  return renderOperations(beforeTokens, afterTokens, ops);
}
